import { AttributeNameEnum } from '@on-arte/core-types';

import { AuctionPublicDetails } from '@onArte/shared/interfaces';

import { findAttributeValue } from './artworkMeta.utils';
import { ArtworkMetaSource, buildArtworkDescription, buildArtworkHeading, escapeHtmlAttribute, toPlainText } from './seoMeta.utils';

/**
 * Feed produktowy Google Merchant Center (osobne zlecenie, poza umową SEO).
 *
 * Tylko atrybuty oznaczone w specyfikacji jako wymagane, których warunek dzieła spełniają
 * (support.google.com/merchants/answer/7052112), plus `identifier_exists` zamiast `mpn`:
 * dzieła nie mają ani kodu EAN, ani numeru katalogowego producenta, a wymyślać go nie wolno.
 * Dostawę ustawia się na koncie Merchant Center, nie w pliku.
 *
 * Do tego pola dla kampanii (Google i Meta) według instrukcji Meta od osoby od reklam:
 * `condition` = new (w katalogu Meta obowiązkowe), `product_type` „sztuka > kategoria" oraz etykiety
 * do budowania zestawów — `custom_label_0` grupa cenowa, `custom_label_1` typ, `custom_label_3`
 * przedział ceny. Wartości dosłownie z instrukcji: filtry w zestawach reklam szukają tych napisów.
 */
export interface MerchantFeedItem {
  id: string;
  title: string;
  description: string;
  path: string;
  imageLink: string;
  price: number;
  brand: string;
  adult: boolean;
  productType: string | null;
  categoryGroup: string;
}

/**
 * Instrukcja Meta, rozdz. 5: wartości małymi literami, bez polskich znaków, z podkreśleniami
 * zamiast spacji („Sztuka cyfrowa" → „sztuka_cyfrowa"). „ł" nie rozkłada się na literę i znak
 * diakrytyczny, więc zamieniamy je osobno.
 */
function toFeedValue(label: string): string {
  return label.toLowerCase().replace(/ł/g, 'l').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, '_');
}

/**
 * Typ dzieła w `custom_label_1` — nazwy z przykładów instrukcji Meta (zestaw „Obrazy premium"
 * filtruje `custom_label_1 = obrazy`). Klucze to nazwy kategorii z API (`items_categories.name`).
 */
const merchantProductTypes: ReadonlyMap<string, string> = new Map([
  ['painting', 'obrazy'],
  ['graphics', 'grafiki'],
  ['sculpture', 'rzezby'],
  ['photography', 'fotografie'],
]);

/** Temat „Akt" — Google wymaga oznaczenia treści dla dorosłych, inaczej grozi zawieszenie konta. */
const adultSubjectKey: string = 'nudes';

/**
 * Pozycja feedu albo `null`, gdy dzieła nie da się zgłosić: bez zdjęcia albo bez ceny
 * Google odrzuci produkt, więc nie ma sensu go wysyłać.
 */
export function buildMerchantFeedItem(
  details: AuctionPublicDetails,
  source: ArtworkMetaSource,
  price: number,
  path: string
): MerchantFeedItem | null {
  if (!source.photoUrl || !(price > 0)) {
    return null;
  }
  const subjects: string[] = (findAttributeValue(details.itemDetails.attributes ?? [], AttributeNameEnum.Subject) ?? '')
    .split(',')
    .map((key: string): string => key.trim());

  return {
    // Identyfikator dzieła, nie aukcji: dzieło wystawione ponownie zostaje tym samym produktem.
    id: details.itemDetails.id,
    title: buildArtworkHeading(source).slice(0, 150),
    description: (toPlainText(source.description) || buildArtworkDescription(source)).slice(0, 5000),
    path,
    imageLink: source.photoUrl,
    price,
    brand: source.artistName.slice(0, 70),
    adult: subjects.includes(adultSubjectKey),
    productType: source.categoryLabel ? `sztuka > ${toFeedValue(source.categoryLabel)}` : null,
    // Pozostałe kategorie (rysunek, design, sztuka cyfrowa…) to „inne".
    categoryGroup: merchantProductTypes.get(details.itemDetails.category?.name ?? '') ?? 'inne',
  };
}

/**
 * Znaki sterujące są w XML niedozwolone nawet jako encje — jeden taki znak w opisie
 * od artysty unieważniłby cały plik, a Google odrzuciłby wtedy wszystkie produkty naraz.
 */
function escapeXml(value: string): string {
  // eslint-disable-next-line no-control-regex
  return escapeHtmlAttribute(value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, ''));
}

/** Granica należy do niższego przedziału: dzieło za 2000 zł jest w „1000_2000". */
function getMerchantPriceBucket(price: number): string {
  if (price <= 500) {
    return 'do_500';
  }
  if (price <= 1000) {
    return '500_1000';
  }

  return price <= 2000 ? '1000_2000' : '2000_plus';
}

/** Grupa cenowa to zgrubny przedział: wszystko powyżej 1000 zł jest „premium". */
const merchantPriceGroups: Record<string, string> = { do_500: 'sztuka_do_500', '500_1000': 'sztuka_500_1000' };

function renderItem(item: MerchantFeedItem, baseUrl: string): string {
  const fields: [string, string][] = [
    ['id', item.id],
    ['title', item.title],
    ['description', item.description],
    ['link', `${baseUrl}${item.path}`],
    ['image_link', item.imageLink],
    ['availability', 'in_stock'],
    ['price', `${item.price.toFixed(2)} PLN`],
    ['brand', item.brand],
    ['identifier_exists', 'no'],
    ['condition', 'new'],
    // Jedna kategoria dla wszystkich dzieł, na prośbę osoby od reklam (29.10.2026): 500044 = „Dom i ogród >
    // Ozdoby do domu i ogrodu > Dzieła sztuki > Plakaty, nadruki, prace plastyczne" w taksonomii Google.
    ['google_product_category', '500044'],
  ];
  if (item.adult) {
    fields.push(['adult', 'yes']);
  }
  if (item.productType) {
    fields.push(['product_type', item.productType]);
  }
  const priceBucket: string = getMerchantPriceBucket(item.price);
  fields.push(
    ['custom_label_0', merchantPriceGroups[priceBucket] ?? 'sztuka_premium'],
    ['custom_label_1', item.categoryGroup],
    ['custom_label_3', priceBucket]
  );

  const tags: string[] = fields.map(([name, value]: [string, string]): string => `  <g:${name}>${escapeXml(value)}</g:${name}>`);

  return `<item>\n${tags.join('\n')}\n</item>`;
}

export function renderMerchantFeed(items: MerchantFeedItem[], baseUrl: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0" xmlns:g="http://base.google.com/ns/1.0">
<channel>
<title>on*arte</title>
<link>${baseUrl}/</link>
<description>Dzieła sztuki współczesnej do kupienia na on*arte</description>
${items.map((item: MerchantFeedItem): string => renderItem(item, baseUrl)).join('\n')}
</channel>
</rss>
`;
}
